import React, { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { CloseIcon, ChevronLeftIcon, ChevronRightIcon } from "../../../../helper/HwIcon";
import imag1 from "../../../../assets/img/no-image-holder.png";

const HotelGalleryPopup = ({ show, setShow, images, initialIndex = 0 }) => {
  const [currentIndex, setCurrentIndex] = useState(initialIndex);
  const [isZoomed, setIsZoomed] = useState(false);
  const [isMainLoading, setIsMainLoading] = useState(true);

  useEffect(() => {
    if (show) {
      setCurrentIndex(initialIndex);
      setIsZoomed(false);
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [show, initialIndex]);

  useEffect(() => {
    if (!images || images.length === 0) return;
    const nextIndex = (currentIndex + 1) % images.length;
    const prevIndex = (currentIndex - 1 + images.length) % images.length;

    const preload = (src) => {
      if (!src) return;
      const img = new Image();
      img.decoding = 'async';
      img.loading = 'eager';
      img.src = src;
    };

    preload(images[nextIndex]);
    preload(images[prevIndex]);
  }, [currentIndex, images]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!show) return;
      
      if (e.key === 'Escape') {
        if (isZoomed) {
          setIsZoomed(false);
        } else {
          setShow(false);
        }
      } else if (e.key === 'ArrowLeft' && !isZoomed) {
        goToPrevious();
      } else if (e.key === 'ArrowRight' && !isZoomed) {
        goToNext();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [show, currentIndex, isZoomed]);

  const goToPrevious = () => {
  const newIndex = currentIndex === 0 ? images.length - 1 : currentIndex - 1;
  if (newIndex !== currentIndex) {
    goToImage(newIndex,isZoomed); // reuse thumbnail + state logic
  }
};


 const goToNext = () => {
  const newIndex = currentIndex === images.length - 1 ? 0 : currentIndex + 1;
  if (newIndex !== currentIndex) {
    goToImage(newIndex,isZoomed); // reuse thumbnail + state logic
  }
};


const thumbnailRefs = useRef([]);

const goToImage = (index,isZoomed) => {
  setIsMainLoading(true);
  setCurrentIndex(index);
  if(!isZoomed) setIsZoomed(false);

  if (!isZoomed) {
    thumbnailRefs.current[index]?.scrollIntoView({
      behavior: "smooth",
      inline: "center",
      block: "nearest",
    });
  }
};

  const handleImageClick = () => {
    setIsZoomed(!isZoomed);
  };

  useEffect(() => {
    setIsMainLoading(true);
  }, [currentIndex]);

  const handleZoomClose = () => {
    setIsZoomed(false);
  };

  if (!show || !images || images.length === 0) return null;

  // Full screen zoom view
  if (isZoomed) {
    return createPortal(
      <>
        <div className="hw-gallery-zoom-overlay" onClick={handleZoomClose}>
          <div className="hw-gallery-zoom-container" onClick={(e) => e.stopPropagation()}>
            <div className="hw-gallery-zoom-header">
              <div className="hw-gallery-zoom-title">
                Image {currentIndex + 1} of {images.length}
              </div>
              <button
                onClick={handleZoomClose}
                className="hw-gallery-zoom-close"
                aria-label="Close zoom view"
              >
                <CloseIcon />
              </button>
            </div>
            
            <div className="hw-gallery-zoom-image-container">
              <button
                className="hw-gallery-zoom-nav-btn hw-gallery-zoom-nav-prev"
                onClick={goToPrevious}
                aria-label="Previous image"
              >
                <ChevronLeftIcon />
              </button>
              
              <div className="hw-gallery-zoom-image" onClick={handleImageClick}>
                <img
                  src={images[currentIndex]}
                  alt={`Hotel image ${currentIndex + 1}`}
                  decoding="async"
                  loading="eager"
                  fetchpriority="high"
                  onLoad={() => setIsMainLoading(false)}
                  onError={(e) => {
                    e.target.src = imag1;
                    setIsMainLoading(false);
                  }}
                />
                {isMainLoading && (
                  <div className="hw-gallery-image-loading">
                    <span style={{minWidth: '2.8rem', minHeight: '2.8rem'}} className='hw-main--loaderCommon' />
                  </div>
                )}
              </div>
              
              <button
                className="hw-gallery-zoom-nav-btn hw-gallery-zoom-nav-next"
                onClick={goToNext}
                aria-label="Next image"
              >
                <ChevronRightIcon />
              </button>
            </div>
            
            <div className="hw-gallery-zoom-instructions">
              Click image to exit full screen • Use arrow keys to navigate • ESC to close
            </div>
          </div>
        </div>
      </>,
      document.body
    );
  }

  return createPortal(
    <>
      <div className="hw-gallery-modal fade show">
        <div className="hw-gallery-modal-dialog">
          <div className="hw-gallery-modal-content">
            {/* Header */}
            <div className="hw-gallery-modal-header">
              <div className="hw-gallery-modal-headerTitle">
                Hotel Gallery ({currentIndex + 1} of {images.length})
              </div>
              <button
                onClick={() => setShow(false)}
                className="hw-gallery-modal-close"
                aria-label="Close gallery"
              >
                <CloseIcon />
              </button>
            </div>

            {/* Main Image Display */}
            <div className="hw-gallery-modal-body">
              <div className="hw-gallery-main-image-container">
                {/* Previous Button */}
                <button
                  className="hw-gallery-nav-btn hw-gallery-nav-prev"
                  onClick={goToPrevious}
                  aria-label="Previous image"
                >
                  <ChevronLeftIcon />
                </button>

                {/* Main Image */}
                <div className="hw-gallery-main-image">
                  <img
                    src={images[currentIndex]}
                    alt={`Hotel image ${currentIndex + 1}`}
                    decoding="async"
                    loading="eager"
                    fetchpriority="high"
                    onLoad={() => setIsMainLoading(false)}
                    onClick={handleImageClick}
                    onError={(e) => {
                      e.target.src = imag1;
                      setIsMainLoading(false);
                    }}
                    className="hw-gallery-clickable-image"
                  />
                  {isMainLoading && (
                    <div className="hw-gallery-image-loading">
                      <span style={{minWidth: '2.8rem', minHeight: '2.8rem'}} className='hw-main--loaderCommon' />
                    </div>
                  )}
                  <div className="hw-gallery-zoom-hint">
                    Click to view full screen
                  </div>
                </div>

                {/* Next Button */}
                <button
                  className="hw-gallery-nav-btn hw-gallery-nav-next"
                  onClick={goToNext}
                  aria-label="Next image"
                >
                  <ChevronRightIcon />
                </button>
              </div>

              {/* Thumbnail Navigation */}
              <div className="hw-gallery-thumbnails">
                {images.map((image, index) => (
                  <div
                    key={index}
                    ref={(el) => (thumbnailRefs.current[index] = el)}
                    className={`hw-gallery-thumbnail ${index === currentIndex ? 'active' : ''}`}
                    onClick={() => goToImage(index,isZoomed)}
                  >
                    <img
                      src={image}
                      alt={`Thumbnail ${index + 1}`}
                      loading="lazy"
                      decoding="async"
                      fetchpriority="low"
                      onError={(e) => {
                        e.target.src = imag1;
                      }}
                    />
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Backdrop */}
      <div className="hw-gallery-modal-backdrop fade show" onClick={() => setShow(false)} />
    </>,
    document.body
  );
};

export default HotelGalleryPopup;
